<template>
  <!-- 2026-08-28：仅渲染编辑内容，描述项必须由订单页面直接声明，避免 Ant Design 忽略包装组件。 -->
  <span v-if="readOnly">{{ modelValue || '—' }}</span>
  <div data-enter-scope="save" v-else class="order-comment-editor">
    <a-textarea
      v-model:value="draft"
      :maxlength="200"
      :placeholder="generateTitle('请输入备注/留言信息')"
      :auto-size="{ minRows: 2, maxRows: 5 }"
      show-count
    />
    <div class="editor-actions">
      <a-button v-enter-action:save="() => saveComment()" type="primary" size="small" :loading="saving" :disabled="!canSave" @click="saveComment">
        {{ generateTitle('保存') }}
      </a-button>
    </div>
  </div>
</template>

<script lang="ts">
import store from '@/store'
import {platformSaleReadOnly} from '@/utils/orderOwnership'
import { computed, defineComponent, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import orderRequest from '@/api/orders'
import { generateTitle } from '@/utils/lang'

export default defineComponent({
  name: 'OrderCommentEditor',
  props: {
    order:{type:Object,default:undefined},
    detailId: {
      type: Number,
      required: true
    },
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    // 2026-08-26：所有管理端订单详情复用同一必填编辑器，保存主单留言且不触碰量体备注。
    const readOnly=computed(()=>platformSaleReadOnly(props.order,store.getters.userInfo))
    const draft = ref('')
    const saving = ref(false)
    // 2026-09-22：保存过程中切换订单后，旧响应不能覆盖当前订单的留言。
    let generation = 0
    watch(
      () => [props.detailId, props.modelValue],
      () => {
        generation++
        saving.value = false
        draft.value = props.modelValue || ''
      },
      { immediate: true }
    )
    const normalizedComment = computed(() => draft.value.trim())
    const canSave = computed(() => !readOnly.value && props.detailId > 0 && normalizedComment.value.length > 0 && !saving.value)
    const saveComment = async () => {
      if (saving.value || readOnly.value) return
      if (!normalizedComment.value) {
        message.warning(generateTitle('请输入备注/留言信息'))
        return
      }
      saving.value = true
      const token = generation
      const submittedComment = normalizedComment.value
      try {
        const response = await orderRequest.updateOrderComments({
          orderDetailId: props.detailId,
          comments: submittedComment,
          // 2026-08-27：提交当前页面看到的原值，避免多人同时编辑时静默覆盖后保存的留言。
          expectedComments: props.modelValue || ''
        })
        if (token !== generation) return
        const savedComment = typeof response.data === 'string' ? response.data : submittedComment
        draft.value = savedComment
        emit('update:modelValue', savedComment)
        message.success(generateTitle('备注/留言信息保存成功'))
      } catch (error: any) {
        if (token !== generation) return
        // 2026-08-27：保存失败必须展示后端原因，避免请求异常时用户只看到按钮没有反应。
        const errorMessage = error?.message || error?.msg || generateTitle('备注/留言信息保存失败，请稍后重试')
        message.error(errorMessage)
      } finally {
        if (token === generation) saving.value = false
      }
    }
    return {readOnly, draft, saving, canSave, saveComment, generateTitle }
  }
})
</script>

<style scoped lang="scss">
.order-comment-editor {
  width: 100%;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
